/**
 * The registry of feature flags: every key that exists, and what it does when nothing says
 * otherwise. Both halves matter — the enum is what stops flag keys becoming magic strings scattered
 * across three packages, and the default is the fail-safe applied when the flag's document is
 * missing or unreadable.
 */

export enum FeatureFlag {
  BuildAPlanTakeover = 'build_a_plan_takeover',
  FeedbackNudge = 'feedback_nudge',
  MealPageRedesign = 'meal_page_redesign'
}

export interface FlagDefinition {
  /**
   * Applied when the flag's document is missing or unreadable. This is a FAIL-SAFE, not a
   * preference: it's what every client falls back to when Firestore can't answer, so it should be
   * the state that's safe to be stuck in.
   */
  default: boolean
  /** Shown beside the toggle in the admin tool, so a flag key isn't self-documenting by guesswork. */
  description: string
}

export const FEATURE_FLAGS: Record<FeatureFlag, FlagDefinition> = {
  [FeatureFlag.BuildAPlanTakeover]: {
    default: false,
    description: 'Full-screen guided plan builder. Off serves the original new-plan dialog.'
  },
  [FeatureFlag.FeedbackNudge]: {
    default: false,
    description: 'One-time popover introducing the header feedback button, about 30s into a session.'
  },
  [FeatureFlag.MealPageRedesign]: {
    default: false,
    description: 'The redesigned meal page. Off serves the current meal page and its design toggle.'
  }
}

/** Every registered key, for iterating the admin page and validating a write. */
export const FEATURE_FLAG_KEYS = Object.values(FeatureFlag)

// Set membership rather than `key in FEATURE_FLAGS`, which would also match inherited keys — a
// write naming 'toString' or '__proto__' would otherwise validate and reach Firestore.
const REGISTERED = new Set<string>(FEATURE_FLAG_KEYS)

export function isFeatureFlag(value: unknown): value is FeatureFlag {
  return typeof value === 'string' && REGISTERED.has(value)
}

/**
 * Resolve one flag from whatever the store returned. Anything unrecognisable — a missing document,
 * a non-boolean `enabled`, a store that failed to load — resolves to the registry default rather
 * than throwing, because a broken flag document must never be able to break a page.
 */
export function resolveFlag(flag: FeatureFlag, stored: Record<string, unknown> | undefined): boolean {
  const enabled = stored?.enabled
  return typeof enabled === 'boolean' ? enabled : FEATURE_FLAGS[flag].default
}
